import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Flex,
  HStack,
  PrimaryButton,
  Spinner,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownToBracketRegular } from '@enechain/ecloud-icons'

import { Modal } from '~/components/common/Modal'
import { iconSize } from '~/constants/iconSize'
import { downloadCsv } from '~/libs/downloadCsv'
import { formatDateTimeHyphen } from '~/models/dateTime'

/** 取得した出力対象と、その基準時点 (= サーバーが返すデータの最終更新時刻)。 */
type Fetched<R> = {
  rows: R[]
  /** 0 件のときはサーバーも返さないため undefined。 */
  dataUpdatedAt: Date | undefined
}

type Props<R> = {
  isOpen: boolean
  onClose: () => void
  /** モーダルタイトル・「データ種別」行に表示する、翻訳済みのデータ種別名。 */
  typeName: string
  /**
   * 出力対象行と基準時点を取得する。モーダルを開いた時点で 1 回だけ呼ばれる
   * (ダウンロード時に再取得しない — 提示した件数・基準時点と実際に落ちる
   * ファイルの内容を一致させるため)。呼び出し元 (種別別 wrapper) が Export
   * RPC 呼び出し + proto 応答 → CSV 行への変換をまとめて担う。
   *
   * `useCallback` で参照を固定して渡すこと (isOpen と併せて open 検知の
   * effect の dependency になる — 再生成されるたびに再取得が走る)。
   */
  exportRows: () => Promise<Fetched<R>>
  /** 取得済み行から CSV 本文 (BOM 抜き) を組み立てる。 */
  buildCsv: (rows: R[]) => string
  /** ダウンロードファイル名を決める (uploads/common.md §6 命名規則)。 */
  fileName: () => string
}

/**
 * 入稿済みデータのダウンロード確認モーダル (uploads/common.md §6 / AC-DUC-028
 * / AC-DUC-032) の種別非依存な骨格。
 *
 * 開いた時点で `exportRows` を 1 回だけ呼び、データ種別名・件数・基準時点を
 * 提示してから「ダウンロード開始」で CSV を組み立てる。出力は全量固定で
 * 絞り込みを持たない (AC-DUC-029)。proto の行形状・RPC 選択・CSV 列組み立ては
 * 種別別 wrapper (`CisContractDownloadModal` 等) が props 経由で注入する
 * ( = 本 component が隠すのは「開く → 1 回取得 → 提示 → ダウンロード」という
 * 手順とその状態遷移で、種別ごとの proto / CSV 知識は持たない)。
 *
 * ダウンロード時に再取得しないのは、提示した件数・基準時点と実際に落ちる
 * ファイルの内容を一致させるため (2 回呼ぶと表示と中身がずれうる)。
 */
export const DownloadModal = <R,>({
  isOpen,
  onClose,
  typeName,
  exportRows,
  buildCsv,
  fileName,
}: Props<R>): React.ReactElement => {
  const { t } = useTranslation()

  const [isPending, setIsPending] = useState(false)
  const [fetched, setFetched] = useState<Fetched<R> | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (!isOpen) {
      setFetched(null)
      setError(null)
      setIsPending(false)
      return
    }
    let aborted = false
    setIsPending(true)
    void (async () => {
      try {
        const result = await exportRows()
        if (!aborted) setFetched(result)
      } catch (err) {
        // 握らないと未処理の Promise 拒否になる。表示は下の Alert が担う。
        if (!aborted) setError(err)
      } finally {
        if (!aborted) setIsPending(false)
      }
    })()
    return () => {
      aborted = true
    }
    // exportRows は呼び出し元 (種別別 wrapper) が useCallback で固定した
    // 安定参照を渡す前提 (useMutation の mutateAsync 等)。isOpen が変わらない限り再実行されない。
  }, [isOpen, exportRows])

  const handleDownload = useCallback(() => {
    if (fetched === null) return
    try {
      downloadCsv(fileName(), buildCsv(fetched.rows))
      onClose()
    } catch (err) {
      // CSV 組み立て / Blob 保存の失敗もモーダル内で見せる (無反応にしない)。
      setError(err)
    }
  }, [fetched, onClose, fileName, buildCsv])

  const footer = (
    <HStack gap={3}>
      <TertiaryButton onClick={onClose}>
        {t('common:action.cancel')}
      </TertiaryButton>
      <PrimaryButton
        disabled={fetched === null || fetched.rows.length === 0}
        onClick={handleDownload}
      >
        <FaArrowDownToBracketRegular boxSize={iconSize.labeledButton} />
        {t('page:adminUploads.export.download')}
      </PrimaryButton>
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={t('page:adminUploads.export.title', { name: typeName })}
    >
      <Box>
        {isPending && (
          <Flex justifyContent="center" paddingY={6} role="progressbar">
            <Spinner />
          </Flex>
        )}

        {error !== null && (
          <Alert
            role="alert"
            status="error"
            title={t('page:adminUploads.export.failed')}
          />
        )}

        {fetched !== null && error === null && (
          <>
            <Box
              padding={3}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
            >
              <SummaryRow
                label={t('page:adminUploads.export.dataType')}
                value={typeName}
              />
              <SummaryRow
                label={t('page:adminUploads.export.rowCount')}
                value={t('page:adminUploads.export.rowCountValue', {
                  count: fetched.rows.length,
                })}
              />
              <SummaryRow
                label={t('page:adminUploads.export.asOf')}
                value={
                  fetched.dataUpdatedAt !== undefined
                    ? formatDateTimeHyphen(fetched.dataUpdatedAt)
                    : '—'
                }
              />
            </Box>
            <Text marginTop={3} color="Text.midEmphasis" fontSize="sm">
              {t('page:adminUploads.export.note')}
            </Text>
          </>
        )}
      </Box>
    </Modal>
  )
}

const SummaryRow = ({
  label,
  value,
}: {
  label: string
  value: string
}): React.ReactElement => (
  <HStack justifyContent="space-between" gap={3}>
    <Text color="Text.midEmphasis" fontSize="sm">
      {label}
    </Text>
    <Text fontSize="sm" fontVariantNumeric="tabular-nums">
      {value}
    </Text>
  </HStack>
)
